import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiTrash2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { translations } from '../translations';

const SelectionTray = ({ selectedEmojis, onRemove, onClear, onCopyAll, lang }) => {
  const listRef = useRef(null);
  const t = translations[lang];

  useEffect(() => {
    if (listRef.current) {
      if (window.innerWidth >= 768) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      } else {
        listRef.current.scrollLeft = listRef.current.scrollWidth;
      }
    }
  }, [selectedEmojis.length]);

  if (selectedEmojis.length === 0) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0, x: '-50%' }}
      animate={{ y: 0, opacity: 1, x: '-50%' }}
      exit={{ y: 100, opacity: 0, x: '-50%' }}
      transition={{ type: "spring", stiffness: 250, damping: 28 }}
      className="fixed bottom-6 left-1/2 z-50 w-[92%] md:w-auto md:min-w-[520px] max-w-[95vw] pointer-events-none"
    >
      <div className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-2xl md:rounded-[2rem] flex items-end p-2 md:p-4 gap-2 md:gap-4 ring-1 ring-white/20 pointer-events-auto">
        
        {/* 計數器 */}
        <div className="shrink-0 pl-2 pb-2 md:pb-3 md:pl-2">
          <div className="flex flex-col items-center justify-center gap-0.5 text-indigo-400 min-w-[3rem]">
            <span className="text-[10px] font-black uppercase tracking-widest opacity-50">Selected</span>
            <span className="text-2xl font-black leading-none tracking-tighter">{selectedEmojis.length}</span>
          </div>
        </div>

        <div className="w-px bg-white/10 self-stretch my-3 mx-1" />

        {/* Emoji 滾動區 */}
        <div className="flex-1 min-w-0">
          <div 
            ref={listRef}
            className="flex items-center gap-2.5 p-1 overflow-x-auto md:overflow-x-hidden md:flex-wrap md:content-start md:overflow-y-auto md:max-h-[160px] scrollbar-hide md:custom-scrollbar w-full"
          >
            <AnimatePresence initial={false} mode='popLayout'>
              {selectedEmojis.map((emoji, index) => (
                <motion.div
                  key={`${emoji}-${index}`}
                  layout
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  className="relative group/item shrink-0"
                >
                  {/* 修正：加入 text-white, font-emoji 以及 drop-shadow-md 確保任何符號在深色底都能清晰顯示 */}
                  <button
                    onClick={() => onRemove(index)}
                    className="w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white/20 rounded-xl md:rounded-2xl flex items-center justify-center text-2xl md:text-3xl text-white font-emoji transition-all border border-white/5 relative"
                  >
                    <span className="drop-shadow-md">{emoji}</span>
                    
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/item:opacity-100 bg-rose-500/90 rounded-xl md:rounded-2xl transition-opacity z-10">
                      <SafeIcon icon={FiTrash2} className="text-white text-base" />
                    </div>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="shrink-0 flex items-center gap-2 pr-1 pb-1">
          <button
            onClick={onClear}
            className="p-3 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl h-11 w-11 flex items-center justify-center transition-all active:scale-90"
            title={t.clearTray}
          >
            <SafeIcon icon={FiTrash2} className="text-xl" />
          </button>
          
          <button
            onClick={onCopyAll}
            className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white h-11 md:h-12 px-5 md:px-8 rounded-xl md:rounded-2xl font-black transition-all shadow-lg shadow-indigo-500/30 whitespace-nowrap active:scale-95 group"
          >
            <SafeIcon icon={FiCopy} className="text-xl group-hover:rotate-12 transition-transform" />
            <span className="hidden sm:inline text-base">{t.copyCombo}</span>
            <span className="sm:hidden text-sm font-bold">複製</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SelectionTray;